import { ThemeEvents } from '@theme/events';

/**
 * Bloc précommande : synchronise précision, modal d’info et classe `preorder` sur le bloc
 * estimation de livraison (masqué en CSS quand actif) selon la variante.
 */
class PreorderInfo extends HTMLElement {
  /** @type {Record<string, { active?: boolean | null; precision?: string; popup_html?: string | null }>} */
  variantsData = {};

  /** @type {ReturnType<typeof setTimeout> | null} */
  #variantUpdatingTimeoutId = null;

  #onVariantUpdated = (/** @type {CustomEvent} */ e) => {
    const pid = e.detail?.data?.productId;
    if (pid && this.dataset.productId && String(pid) !== String(this.dataset.productId)) return;
    const vid = e.detail?.resource?.id;
    if (vid != null) this.#applyVariant(String(vid));
  };

 
  #onVariantSelected = (/** @type {Event} */ e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const picker = target.closest('variant-picker');
    if (!picker) return;
    const pid = picker.dataset.productId;
    if (!pid || !this.dataset.productId || String(pid) !== String(this.dataset.productId)) return;
    if (this.hasAttribute('data-visual-preview-mode')) return;

    this.setAttribute('data-variant-updating', '');
    if (this.#variantUpdatingTimeoutId != null) {
      clearTimeout(this.#variantUpdatingTimeoutId);
    }
    this.#variantUpdatingTimeoutId = setTimeout(() => {
      this.removeAttribute('data-variant-updating');
      this.#variantUpdatingTimeoutId = null;
    }, 8000);
  };

  connectedCallback() {
    const script = this.querySelector('[data-preorder-variants]');
    if (!script?.textContent) return;
    try {
      this.variantsData = JSON.parse(script.textContent);
    } catch {
      this.variantsData = {};
    }
    document.addEventListener(ThemeEvents.variantUpdated, this.#onVariantUpdated);
    document.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    const initial = this.dataset.initialVariantId;
    if (initial) this.#applyVariant(String(initial));
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdated, this.#onVariantUpdated);
    document.removeEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    if (this.#variantUpdatingTimeoutId != null) {
      clearTimeout(this.#variantUpdatingTimeoutId);
      this.#variantUpdatingTimeoutId = null;
    }
  }

  /**
   * @param {string} variantId
   */
  #applyVariant(variantId) {
    const data = this.variantsData[variantId] ?? null;
    const active = data?.active === true;

    const showPrecision = this.dataset.showPrecision !== 'false';
    const precisionEl = this.querySelector('[data-ref="preorder-precision"]');
    if (precisionEl && showPrecision) {
      const prec = data?.precision;
      if (prec) {
        precisionEl.textContent = prec;
        precisionEl.hidden = false;
      } else {
        precisionEl.textContent = '';
        precisionEl.hidden = true;
      }
    }

    const body = this.querySelector('[data-ref="preorder-modal-body"]');
    const trigger = this.querySelector('[data-ref="popup-toggle"]');
    if (body && trigger) {
      const html = data?.popup_html;
      if (html) {
        body.innerHTML = html;
        trigger.hidden = false;
      } else {
        body.innerHTML = '';
        trigger.hidden = true;
      }
    }

    const row = this.querySelector('[data-ref="preorder-row"]');
    const hasPrecision = showPrecision && Boolean(data?.precision);
    const hasPopup = Boolean(data?.popup_html);
    const hideRow = !active || (!hasPrecision && !hasPopup);
    if (row) {
      row.hidden = hideRow;
    }

    if (!this.hasAttribute('data-visual-preview-mode')) {
      this.hidden = hideRow;
    }

    const selector = this.dataset.deliverySelector || '.delivery-estimation';
    const sectionRoot =
      this.closest('[data-ref="product-section-content"]') ||
      this.closest('.product-section__product-info') ||
      this.closest('.shopify-section');
    const target = sectionRoot?.querySelector(selector);
    if (target) {
      target.classList.toggle('preorder', active);
    }

    if (this.#variantUpdatingTimeoutId != null) {
      clearTimeout(this.#variantUpdatingTimeoutId);
      this.#variantUpdatingTimeoutId = null;
    }
    this.removeAttribute('data-variant-updating');
  }
}

if (!customElements.get('preorder-info')) {
  customElements.define('preorder-info', PreorderInfo);
}
